/**
 * Variant Discount Badge
 * -----------------------
 * Renders the per-option-value discount / loyalty badges on the variant picker
 * and keeps them correct as the shopper changes their selection.
 *
 * THE RULE (mirrors assets/variant-availability.js exactly)
 * For an option value V at picker position P, the set of "relevant" variants is:
 *     - options BEFORE P : must equal the currently selected value
 *     - option  AT     P : must equal V
 *     - options AFTER  P : wildcard (any value)
 * The badge is shown only when at least one relevant variant is AVAILABLE and
 * carries the flag. Loyalty takes priority over a compare-at (crossout) discount.
 *
 * Why this is the reliable rule:
 *   - It is the same pattern variant-availability.js uses to decide whether a
 *     pill is shown / sold-out, so a badge can never appear on a pill the shopper
 *     sees as sold-out, and the secondary option never bleeds another option's
 *     status onto an unavailable combination.
 *   - Because later options are wildcarded, the PRIMARY option's badges depend
 *     only on the option itself (not on the other selections), so they never
 *     shift when a secondary option auto-changes.
 *   - The SECONDARY option legitimately depends on the selected primary, exactly
 *     like its availability does, so its badges narrow correctly on selection.
 *
 * Pricing rules stay in Liquid: snippets/main-product-variant-picker.liquid emits
 * per-variant `loyalty` / `discount` booleans in the [data-variant-options] JSON,
 * so cross-out vs. loyalty pricing is never re-implemented here.
 */
(function () {
  'use strict';

  var BADGE_SELECTOR = '.variant-discount-badge';
  var INIT_FLAG = 'discountBadgeInitialized';

  function VariantDiscountBadge(picker) {
    this.picker = picker;

    var dataScript = picker.querySelector('[data-variant-options]');
    if (!dataScript) {
      return;
    }

    try {
      this.data = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    if (!this.data || !this.data.variants || !this.data.options) {
      return;
    }

    this.optionCount = this.data.options.length;
    this.loyaltyIcon = picker.getAttribute('data-loyalty-badge-icon') || '';

    // Normalise variants once for fast matching.
    this.variants = [];
    for (var i = 0; i < this.data.variants.length; i++) {
      var v = this.data.variants[i];
      this.variants.push({
        options: v.options,
        available: v.available === true,
        loyalty: v.loyalty === true,
        discount: v.discount === true
      });
    }

    var self = this;
    this.scheduledUpdate = false;

    this.picker.addEventListener('change', function () {
      self.scheduleUpdate();
    });

    // Initial render (the picker is hidden until JS, so first paint is here).
    this.scheduleUpdate();
  }

  /**
   * Coalesce the burst of change events fired while variant-availability.js
   * auto-selects options into a single update on the next frame, so we always
   * read the settled selection.
   */
  VariantDiscountBadge.prototype.scheduleUpdate = function () {
    if (this.scheduledUpdate) {
      return;
    }
    this.scheduledUpdate = true;

    var self = this;
    var run = function () {
      self.scheduledUpdate = false;
      self.update();
    };

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 16);
    }
  };

  /**
   * Currently selected value for each option position, by fieldset order
   * (which matches the option order used in each variant's `options` array).
   */
  VariantDiscountBadge.prototype.getSelectedOptions = function () {
    var selected = [];
    var fieldsets = this.picker.querySelectorAll('.product-form__input--variant-picker');

    for (var i = 0; i < fieldsets.length; i++) {
      var input = fieldsets[i].querySelector('input[type="radio"]:checked, option:checked, option[selected]');
      selected[i] = input ? input.value : null;
    }

    return selected;
  };

  /**
   * Aggregate badge state for value `optionValue` at position `optionIndex`,
   * using the mirror-availability pattern described in the file header.
   * Returns 'loyalty', 'discount', or null.
   */
  VariantDiscountBadge.prototype.resolveBadge = function (optionIndex, optionValue, selected) {
    var hasLoyalty = false;
    var hasDiscount = false;

    for (var i = 0; i < this.variants.length; i++) {
      var variant = this.variants[i];

      if (!variant.available) {
        continue;
      }

      var matches = true;
      for (var p = 0; p < this.optionCount; p++) {
        if (p < optionIndex) {
          // Earlier options must equal the current selection.
          if (selected[p] === null || selected[p] === undefined || variant.options[p] !== selected[p]) {
            matches = false;
            break;
          }
        } else if (p === optionIndex) {
          if (variant.options[p] !== optionValue) {
            matches = false;
            break;
          }
        }
        // Later options are wildcards.
      }

      if (!matches) {
        continue;
      }

      if (variant.loyalty) {
        hasLoyalty = true;
        break; // Loyalty wins outright; nothing can override it.
      }
      if (variant.discount) {
        hasDiscount = true;
      }
    }

    if (hasLoyalty) {
      return 'loyalty';
    }
    if (hasDiscount) {
      return 'discount';
    }
    return null;
  };

  VariantDiscountBadge.prototype.update = function () {
    var selected = this.getSelectedOptions();
    var fieldsets = this.picker.querySelectorAll('.product-form__input--variant-picker');

    for (var f = 0; f < fieldsets.length; f++) {
      var pills = fieldsets[f].querySelectorAll('.dynamic-option');

      // Single-value options never show a badge.
      if (pills.length <= 1) {
        for (var s = 0; s < pills.length; s++) {
          this.clearBadge(pills[s]);
        }
        continue;
      }

      for (var p = 0; p < pills.length; p++) {
        var pill = pills[p];
        var input = pill.querySelector('input[type="radio"], option');
        if (!input) {
          continue;
        }

        var type = this.resolveBadge(f, input.value, selected);

        if (type) {
          this.renderBadge(pill, type);
        } else {
          this.clearBadge(pill);
        }
      }
    }
  };

  VariantDiscountBadge.prototype.clearBadge = function (pill) {
    var badge = pill.querySelector(BADGE_SELECTOR);
    if (badge) {
      badge.parentNode.removeChild(badge);
    }
  };

  VariantDiscountBadge.prototype.renderBadge = function (pill, type) {
    var badge = pill.querySelector(BADGE_SELECTOR);

    if (!badge) {
      badge = document.createElement('span');
      badge.setAttribute('aria-hidden', 'true');
      pill.appendChild(badge);
    }

    // Skip rewriting identical content (avoids re-requesting the icon image).
    if (badge.getAttribute('data-type') === type) {
      return;
    }

    badge.setAttribute('data-type', type);

    if (type === 'loyalty') {
      badge.className = 'variant-discount-badge variant-discount-badge--loyalty';
      badge.textContent = '';
      if (this.loyaltyIcon) {
        var img = document.createElement('img');
        img.src = this.loyaltyIcon;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 14;
        img.height = 14;
        badge.appendChild(img);
      }
    } else {
      badge.className = 'variant-discount-badge';
      badge.textContent = '%';
    }
  };

  function initPicker(picker) {
    if (!picker || picker.dataset[INIT_FLAG] === 'true') {
      return;
    }
    picker.dataset[INIT_FLAG] = 'true';
    // eslint-disable-next-line no-new
    new VariantDiscountBadge(picker);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var pickers = scope.querySelectorAll('variant-radios');
    for (var i = 0; i < pickers.length; i++) {
      initPicker(pickers[i]);
    }
  }

  function start() {
    initAll(document);

    // Catch pickers injected later (quick view / quick add drawers, etc.).
    if (window.MutationObserver) {
      var observer = new MutationObserver(function (mutations) {
        for (var m = 0; m < mutations.length; m++) {
          var added = mutations[m].addedNodes;
          for (var n = 0; n < added.length; n++) {
            var node = added[n];
            if (node.nodeType !== 1) {
              continue;
            }
            if (node.matches && node.matches('variant-radios')) {
              initPicker(node);
            } else if (node.querySelectorAll) {
              initAll(node);
            }
          }
        }
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  // Guard against the script being included more than once on a page.
  if (window.__variantDiscountBadgeStarted) {
    initAll(document);
  } else {
    window.__variantDiscountBadgeStarted = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }
})();
